import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import useTheme from 'hooks/useTheme'
import { LineChartLoader } from 'components/ChartLoaders'
import { createChart, IChartApi, UTCTimestamp } from 'lightweight-charts'
import { format } from 'date-fns'
import { darken } from 'polished'
import { useTranslation } from '@pancakeswap/localization'
import { formatAmount } from 'utils/formatInfoNumbers'
import { lightColors, darkColors } from '@pancakeswap/ui/tokens/colors'

export type LineChartProps = {
  data: any[]
  setHoverValue: Dispatch<SetStateAction<number | undefined>> // used for value on hover
  setHoverDate: Dispatch<SetStateAction<string | undefined>> // used for label of value
} & React.HTMLAttributes<HTMLDivElement>

/**
 * Note: remember that it needs to be mounted inside the container with fixed height
 */
const LineChart = ({ data, setHoverValue, setHoverDate }: LineChartProps) => {
  const { isDark } = useTheme()
  const {
    currentLanguage: { locale },
  } = useTranslation()
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartCreated, setChart] = useState<IChartApi | undefined>()

  const transformedData = useMemo(() => {
    if (data) {
      return data.map(({ time, value }) => {
        return {
          // lightweight-charts takes UTC seconds; milliseconds made the time scale think a month was millennia (ticks read 58639)
          time: Math.floor(time.getTime() / 1000) as UTCTimestamp,
          value,
        }
      })
    }
    return []
  }, [data])

  useEffect(() => {
    if (!chartRef?.current || !transformedData || transformedData.length === 0) return

    const chart = createChart(chartRef?.current, {
      layout: {
        background: { color: 'transparent' },
        textColor: isDark ? darkColors.textSubtle : lightColors.textSubtle,
      },
      autoSize: true,
      handleScale: false,
      handleScroll: false,
      rightPriceScale: {
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        borderVisible: false,
      },
      timeScale: {
        visible: true,
        borderVisible: false,
        secondsVisible: false,
        // the library says what kind of tick it is (0 year, 1 month, 2 day of month, 3 time): a day series gets days, an hourly one gets times
        tickMarkFormatter: (unixTime: number, tickMarkType: number) => {
          return format(unixTime * 1000, tickMarkType === 0 ? 'yyyy' : tickMarkType === 1 ? 'MMM' : tickMarkType === 2 ? 'd MMM' : 'h:mm a')
        },
      },
      grid: {
        horzLines: {
          visible: false,
        },
        vertLines: {
          visible: false,
        },
      },
      crosshair: {
        horzLine: {
          visible: true,
          labelVisible: true,
        },
        mode: 1,
        vertLine: {
          visible: true,
          labelVisible: false,
          style: 3,
          width: 1,
          color: isDark ? darkColors.textSubtle : lightColors.textSubtle,
        },
      },
    })

    chart.applyOptions({
      localization: {
        priceFormatter: (priceValue) => formatAmount(priceValue),
      },
    })

    const newSeries = chart.addAreaSeries({
      lineWidth: 2,
      lineColor: isDark ? darkColors.secondary : lightColors.secondary,
      topColor: darken(0.01, isDark ? darkColors.secondary : lightColors.secondary),
      bottomColor: isDark ? darkColors.backgroundDisabled : lightColors.backgroundDisabled,
      priceFormat: {
        type: 'price',
        precision: 4,
        minMove: 0.0001,
      },
    })
    setChart(chart)
    newSeries.setData(transformedData)

    chart.timeScale().fitContent()

    chart.subscribeCrosshairMove((param) => {
      if (newSeries && param) {
        const timestamp = param.time as number
        if (!timestamp) return
        const now = new Date(timestamp * 1000)
        const time = `${now.toLocaleString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: 'UTC',
        })} (UTC)`
        // @ts-ignore
        const parsed = (param.seriesData.get(newSeries)?.value ?? 0) as number | undefined
        if (setHoverValue) setHoverValue(parsed)
        if (setHoverDate) setHoverDate(time)
      } else {
        if (setHoverValue) setHoverValue(undefined)
        if (setHoverDate) setHoverDate(undefined)
      }
    })

    // eslint-disable-next-line consistent-return
    return () => {
      chart.remove()
    }
  }, [isDark, locale, transformedData, setHoverValue, setHoverDate])

  return (
    <>
      {!chartCreated && <LineChartLoader />}
      <div style={{ display: 'flex', flex: 1, height: '100%' }}>
        <div style={{ flex: 1, maxWidth: '100%' }} ref={chartRef} />
      </div>
    </>
  )
}

export default LineChart
